import {
  useContext, useEffect, useState, ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';

import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Chip, { ChipProps } from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';

import {
  formatCurrency,
  formatDate,
} from '#common/utils';
import {
  FULFILLMENT_STATUS,
  FULFILLMENT_COLOR,
  FULFILLMENT_LITERAL,
} from '#common/constants';
import URLSearchParamsEx from '#utils/url-ex';
import {
  useGetInventoryOrdersQuery,
} from '#services/inventory';
import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
  toDateRangeQueryParams,
} from '#components/time-range.utils';
import DropdownFilterBox from '#components/DropdownFilterBox';
import { FeedbackUIContext } from '#context/feedback-ui-context';

import {
  setDateRange,
  setFilterKey,
  setFilter,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setSelected,
} from './inventory.slice';

interface InventoryOrderRow {
  id: string | number;
  createdByUserName?: string;
  createdByUserCode?: string;
  availableDate?: string;
  createdDate?: string;
  receivings: Array<string | number>;
  totalPrice: number;
  fulfillmentStatus: number;
  [key: string]: unknown;
}

interface InventoryOrderListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  selected: Array<string | number | undefined>;
  vendors: Record<string, ReactNode>;
  skip: boolean;
}

export default function AdminInventoryOrderList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userInfo = useSelector((state: { auth: { userInfo: { isAdmin?: boolean } } }) => state.auth.userInfo);
  const filterKey = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.filterKey);
  const filter = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.filter);
  const dateRange = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.dateRange);
  const headerFilters = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.headerFilters);
  const order = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.order);
  const orderBy = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.orderBy);
  const pageIndex = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.pageIndex);
  const pageSize = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.pageSize);
  const rows = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.rows);
  const rowCount = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.rowCount);
  const selected = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.selected);
  const vendors = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.vendors);
  const skip = useSelector((state: { inventoryOrderList: InventoryOrderListSlice }) => state.inventoryOrderList.skip);
  const [debouncedPageIndex] = useDebounce(pageIndex, 300);
  const [filterForQuery, setFilterForQuery] = useState(filter);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const {
    showModal,
    hideModal,
    setModalData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dateRangeParam = resolveDateRangeParam('createdDate', dateRange);

  const query = useGetInventoryOrdersQuery({
    dateRange: dateRangeParam,
    order,
    orderBy,
    filter: filterForQuery,
    filterKey,
    headerFilters,
    pageIndex: debouncedPageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
    skip,
  });

  // Restore state from URL parameters on component mount only
  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      setFilterForQuery(urlParams.values.filter as string);
    }
    if (urlParams.values.filterKey !== undefined) {
      dispatch(setFilterKey(urlParams.values.filterKey as string));
    }
    if (urlParams.values.headerFilters !== undefined) {
      dispatch(setHeaderFilters(urlParams.values.headerFilters as Record<string, Record<string, boolean>>));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as unknown as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  // Sync filterForQuery with filter when filter changes
  useEffect(() => {
    setFilterForQuery(filter);
  }, [filter]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const handleInventoryOrdersExport = () => {
    // WORKAROUND react state cannot work, use local variable instead
    let exportTargetOption = 'current';

    setModalData({
      children: (
        <>
          <Typography variant="h6" gutterBottom>
            Export Orders
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <FormControl sx={{ mb: 5 }}>
            <FormLabel id="label-export-radio">Export</FormLabel>
            <RadioGroup
              aria-labelledby="label-export-radio"
              defaultValue="current"
              name="radio-buttons-group"
              onChange={(event, value) => {
                exportTargetOption = value;
              }}
            >
              <FormControlLabel value="current" control={<Radio />} label="Current page" />
              <FormControlLabel value="all" control={<Radio />} label="All orders" />
              <FormControlLabel
                disabled={!selected.length}
                value="selected"
                control={<Radio />}
                label={`Selected: ${selected.length} inventory orders`}
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={hideModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                let additionalParam = '';
                if (exportTargetOption === 'current') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&page=${pageIndex}&pageSize=${pageSize}`;
                } else if (exportTargetOption === 'all') {
                  additionalParam = `order=${order}&orderBy=${orderBy}&filter=${filter}&all=1`;
                } else if (exportTargetOption === 'selected') {
                  additionalParam = `inventoryOrderIds=${selected.join(',')}`;
                }

                // An explicit selection is already the exact row set the user
                // picked, so only the page/all exports carry the table's range.
                if (exportTargetOption !== 'selected') {
                  const rangeParams = new URLSearchParams(
                    toDateRangeQueryParams(dateRangeParam),
                  ).toString();
                  if (rangeParams) {
                    additionalParam = `${additionalParam}&${rangeParams}`;
                  }
                }

                const exportUrl = `/api/export/inventoryOrders?${additionalParam}`;
                window.open(exportUrl, '_blank');
                hideModal();
              }}
            >
              Export orders
            </Button>
          </Stack>
        </>
      ),
      style: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
      },
    });
    showModal();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'id',
    label: 'Order #',
    renderer: (row, idx) => {
      const o = row as unknown as InventoryOrderRow;
      const inventoryOrderIndex = pageIndex * pageSize + idx + 1;
      const qs = new URLSearchParamsEx({
        order,
        orderBy,
        filterKey,
        filter,
        headerFilters,
        inventoryOrderId: o.id,
        inventoryOrderIndex,
        // The detail page re-runs the list query to resolve the row index (and
        // to walk prev/next), so it needs the same date window the list used —
        // without it the index points into the unfiltered list.
        ...(dateRangeParam ? { dateRange: dateRangeParam } : {}),
      });
      const url = `/management/inventory/detail?${qs.toString()}`;
      return (
        <Link
          href={url}
          onClick={(e) => {
            e.preventDefault();
            void navigate(url);
          }}
          sx={{ textDecoration: 'inherit' }}
        >
          {o.id}
        </Link>
      );
    },
  }, {
    id: 'createdByUserName',
    label: 'Created By',
    renderer: (row) => {
      const o = row as unknown as InventoryOrderRow;
      return `${o.createdByUserName}#${o.createdByUserCode}`;
    },
  }, {
    id: 'vendor',
    label: 'Vendor',
    filters: vendors,
  }, {
    id: 'availableDate',
    label: 'Available date',
    renderer: (row) => {
      const { availableDate } = row as unknown as InventoryOrderRow;
      if (!availableDate) {
        return '-';
      }

      try {
        return formatDate(availableDate);
      } catch (_err) {
        return 'UNAVAILABLE';
      }
    },
  }, {
    id: 'createdDate',
    label: 'Created Date',
    renderer: (row) => {
      try {
        const { createdDate } = row as unknown as InventoryOrderRow;
        return formatDate(createdDate as string);
      } catch (_err) {
        return 'UNAVAILABLE';
      }
    },
  }, {
    id: 'receivings',
    label: 'Receiving List',
    sortable: false,
    renderer: (row) => {
      const { receivings } = row as unknown as InventoryOrderRow;
      return receivings.map((receivingId) => {
        const url = `/management/receiving/edit?receivingId=${receivingId}`;
        return (
          <Link
            key={receivingId}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
            sx={{
              ml: 0.5,
              textDecoration: 'inherit',
            }}
          >
            {receivingId}
          </Link>
        );
      });
    },
  }, {
    id: 'totalPrice',
    label: 'Order Total',
    renderer: (row) => `${formatCurrency((row as unknown as InventoryOrderRow).totalPrice)}`,
  }, {
    id: 'fulfillmentStatus',
    label: 'Order Status',
    filters: FULFILLMENT_LITERAL,
    renderer: (row) => {
      const o = row as unknown as InventoryOrderRow;
      return (
        <Chip
          size="small"
          label={FULFILLMENT_LITERAL[o.fulfillmentStatus]}
          color={FULFILLMENT_COLOR[o.fulfillmentStatus] as ChipProps['color']}
        />
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    headers: tableHeaders,
    pinnedColumns: {
      left: ['id'],
    },
    enableSort: true,
    order,
    setOrder,
    orderBy,
    setOrderBy,

    headerFilters,
    setHeaderFilters,

    getRowStyle: (row) => (
      ((row as unknown as InventoryOrderRow).fulfillmentStatus === FULFILLMENT_STATUS.CANCELED)
        ? { textDecoration: 'line-through' }
        : {}
    ),

    hasSelectColumn: true,
    selected,
    setSelected,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Created Date',
      defaultPreset: DEFAULT_TIME_RANGE_PRESET,
      entityLabel: 'inventory orders',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Inventory Orders
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >

        <DropdownFilterBox
          width={500}
          dropdownItems={[{
            value: 'inventoryOrderId',
            text: 'Inventory Order #',
          }, {
            value: 'orderId',
            text: 'Customer Order #',
          }, {
            value: 'style',
            text: 'Style #',
          }]}
          filter={filter}
          filterKey={filterKey}
          filterPlaceholder="Filter orders"
          dispatch={dispatch as (action: unknown) => void}
          setFilter={setFilter}
          setFilterKey={setFilterKey}
          setFilterForQuery={setFilterForQuery}
        />

        <ButtonGroup variant="outlined" size="small">
          <Button
            disabled={!userInfo.isAdmin}
            onClick={() => { void navigate('/shop?inventory=true'); }}
          >
            Create Inventory Order
          </Button>
          <Button
            onClick={handleInventoryOrdersExport}
          >
            Export
          </Button>
        </ButtonGroup>
      </Box>

      <EnhancedTable
        name="inventory-list"
        {...tableProps}
      />
    </>
  );
}
